"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { CheckCircle2, Plus, Trash2, XCircle } from "lucide-react";

import { useAuth } from "@/components/providers/auth-provider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type { FunctionSignature } from "@/features/judge/lib/function-signature";
import { checkFunctionTest, parseValue, splitCalls } from "@/features/judge/lib/function-value";
import {
  useFunctionTestIssueText,
  useValueRefusalText,
} from "@/features/judge/components/workspace/function-value-text";
import { useContestProblem } from "../../hooks";

/**
 * The sheet's input on a function problem: the signature from the problem's
 * own view (the one the problem page reads and caches), a skeleton while it
 * loads, the fields once it has, and the plain box if it cannot be read (the
 * server still reads the hack against the signature).
 */
export function UphackFunctionSection({
  slug,
  letter,
  value,
  fileStamp,
  onChange,
}: {
  slug: string;
  letter: string;
  value: string;
  /** Moves when a file is picked, so the fields open again on what it holds. */
  fileStamp: number;
  onChange: (next: { input: string; ready: boolean }) => void;
}) {
  const t = useTranslations("contest.uphack");
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const viewerKey = authLoading ? "loading" : isAuthenticated && user ? `member:${user._id}` : "anon";
  const view = useContestProblem(slug, letter, viewerKey, !authLoading);
  const signature = view.data?.signature ?? null;
  const unreadable = view.isError || (view.isSuccess && !signature);

  // Without the signature the box is a plain one, and any text may be sent.
  useEffect(() => {
    if (unreadable) onChange({ input: value, ready: value.trim().length > 0 });
    // Only when the read settles: the box reports its own changes below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadable]);

  if (signature) {
    return (
      <div className="space-y-1.5">
        <p className="text-sm font-medium text-neutral-800">{t("fnYourInput")}</p>
        <UphackFunctionInput key={fileStamp} signature={signature} value={value} onChange={onChange} />
      </div>
    );
  }
  if (unreadable) {
    return (
      <label className="block space-y-1.5">
        <span className="text-sm font-medium text-neutral-800">{t("yourInput")}</span>
        <Textarea
          value={value}
          onChange={(event) => onChange({ input: event.target.value, ready: event.target.value.trim().length > 0 })}
          rows={7}
          spellCheck={false}
          className="font-mono text-sm"
        />
      </label>
    );
  }
  return (
    <div className="space-y-2" aria-busy="true" data-testid="uphack-signature-loading">
      <div className="h-4 w-48 animate-pulse rounded bg-neutral-100" />
      <div className="h-24 animate-pulse rounded-xl bg-neutral-100" />
    </div>
  );
}

/**
 * A hack on a function problem, typed by parameter - `EX-FNC06`, `docs/27`
 * §9.38. The input is still the file the judge reads (one line per
 * parameter per call); this draws each call as one field per parameter, each
 * read as the judge reads it, and keeps "As text" for a long generated input
 * or a file. `onChange` gets the file and whether every call reads, so the
 * sheet keeps its send off until it does; the server reads it again.
 */
export function UphackFunctionInput({
  signature,
  value,
  onChange,
}: {
  signature: FunctionSignature;
  value: string;
  onChange: (next: { input: string; ready: boolean }) => void;
}) {
  const t = useTranslations("contest.uphack");
  const issueText = useFunctionTestIssueText();
  const [mode, setMode] = useState<"fields" | "raw">("fields");
  const [calls, setCalls] = useState<string[][]>(() => callsOf(signature, value));

  const write = (next: string[][]) => {
    setCalls(next);
    onChange({ input: inputOf(next), ready: callsRead(signature, next) });
  };
  const switchMode = (next: "fields" | "raw") => {
    if (next === "fields") {
      const fresh = callsOf(signature, value);
      setCalls(fresh);
      onChange({ input: inputOf(fresh), ready: callsRead(signature, fresh) });
    }
    setMode(next);
  };
  const rawIssue = mode === "raw" ? inputIssue(signature, value) : null;

  return (
    <div className="space-y-3" data-testid="uphack-function-input">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="min-w-0 truncate font-mono text-xs text-neutral-600" title={signatureLine(signature)}>
          {signatureLine(signature)}
        </p>
        <SegmentedControl
          label={t("fnViewLabel")}
          size="sm"
          value={mode}
          onChange={switchMode}
          options={[
            { value: "fields", label: t("fnViewFields") },
            { value: "raw", label: t("fnViewRaw") },
          ]}
        />
      </div>

      {mode === "fields" ? (
        <div className="space-y-2.5">
          {calls.map((call, callIndex) => (
            <div
              key={callIndex}
              className="space-y-2 rounded-xl border border-neutral-200 bg-white p-3 motion-safe:animate-in motion-safe:fade-in"
              data-testid="uphack-call"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-bold text-neutral-800">{t("fnCall", { idx: callIndex + 1 })}</p>
                {calls.length <= 1 ? null : (
                  <Button
                    type="button"
                    variant="tertiary"
                    size="smIcon"
                    aria-label={t("fnRemoveCall", { idx: callIndex + 1 })}
                    onClick={() => write(calls.filter((_, at) => at !== callIndex))}
                    className="hover:text-red-600"
                  >
                    <Trash2 aria-hidden className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {signature.params.map((param, paramIndex) => (
                <ParamField
                  key={param.name}
                  id={`uphack-${callIndex}-${param.name}`}
                  name={param.name}
                  type={param.type}
                  value={call[paramIndex] ?? ""}
                  onChange={(next) =>
                    write(calls.map((entry, at) => (at === callIndex ? entry.map((old, index) => (index === paramIndex ? next : old)) : entry)))
                  }
                />
              ))}
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => write([...calls, signature.params.map(() => "")])}>
            <Plus aria-hidden className="h-3.5 w-3.5" />
            {t("fnAddCall")}
          </Button>
        </div>
      ) : (
        <div className="space-y-1.5">
          <Textarea
            value={value}
            onChange={(event) => onChange({ input: event.target.value, ready: inputIssue(signature, event.target.value) === null })}
            rows={7}
            spellCheck={false}
            aria-label={t("yourInput")}
            placeholder={signature.params.map((param) => `${param.name}: ${param.type}`).join("\n")}
            className="font-mono text-sm"
          />
          {rawIssue ? (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800" data-testid="uphack-function-issue">
              {issueText(rawIssue)}
            </p>
          ) : null}
        </div>
      )}
      <p className="text-xs leading-relaxed text-neutral-500">{t("fnHint")}</p>
    </div>
  );
}

function ParamField({
  id,
  name,
  type,
  value,
  onChange,
}: {
  id: string;
  name: string;
  type: FunctionSignature["params"][number]["type"];
  value: string;
  onChange: (value: string) => void;
}) {
  const t = useTranslations("contest.uphack");
  const refusalText = useValueRefusalText();
  const empty = value.trim() === "";
  const reading = empty ? null : parseValue(value, type);
  const bad = reading !== null && !reading.ok;
  return (
    <div className="grid gap-x-3 gap-y-1 sm:grid-cols-[110px_minmax(0,1fr)_auto] sm:items-center" data-testid="uphack-field">
      <label htmlFor={id} className="min-w-0 font-mono text-[12.5px] text-neutral-700">
        <span className="block truncate">{name}</span>
        <span className="block text-[11px] text-neutral-400">{type}</span>
      </label>
      <Input
        id={id}
        value={value}
        spellCheck={false}
        autoComplete="off"
        aria-invalid={bad ? true : undefined}
        aria-describedby={bad ? `${id}-why` : undefined}
        onChange={(event) => onChange(event.target.value)}
        className={cn("font-mono", bad && "border-red-400 bg-red-50/40")}
      />
      <span
        data-state={empty ? "empty" : bad ? "bad" : "ok"}
        className={cn(
          "inline-flex items-center gap-1 justify-self-start rounded-full px-2 py-0.5 text-[11.5px] font-semibold transition-colors duration-200",
          empty ? "bg-neutral-100 text-neutral-500" : bad ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700",
        )}
      >
        {empty ? null : bad ? <XCircle aria-hidden className="h-3 w-3" /> : <CheckCircle2 aria-hidden className="h-3 w-3" />}
        {empty ? t("fnEmpty") : bad ? t("fnBad") : t("fnOk")}
      </span>
      {reading && !reading.ok ? (
        <p id={`${id}-why`} className="text-xs text-red-700 sm:col-span-2 sm:col-start-2">
          {refusalText(reading.refusal)}
        </p>
      ) : null}
    </div>
  );
}

/** `maxProfit(prices: int[]) -> int`, as the paper and the card print it. */
export function signatureLine(signature: FunctionSignature): string {
  return `${signature.name}(${signature.params.map((param) => `${param.name}: ${param.type}`).join(", ")}) -> ${signature.returns}`;
}

/** The fields' calls from a file; a new hack starts on one empty call. */
function callsOf(signature: FunctionSignature, input: string): string[][] {
  const calls = splitCalls(signature, input, "").map((call) => call.params);
  return calls.length > 0 ? calls : [signature.params.map(() => "")];
}

/** The file the judge reads: one line per parameter per call. */
export function inputOf(calls: readonly string[][]): string {
  const lines = calls.flatMap((call) => call.map((value) => value.trim()));
  return lines.length > 0 ? `${lines.join("\n")}\n` : "";
}

/** Every field filled in and read as its type. */
export function callsRead(signature: FunctionSignature, calls: readonly string[][]): boolean {
  return (
    calls.length > 0 &&
    calls.every((call) =>
      signature.params.every((param, index) => {
        const value = call[index] ?? "";
        return value.trim() !== "" && parseValue(value, param.type).ok;
      }),
    )
  );
}

/** Why the file's input does not read as whole calls, or `null`; the answers are the setter's, so never asked for. */
function inputIssue(signature: FunctionSignature, input: string) {
  const issue = checkFunctionTest(signature, input, "");
  return issue && issue.stream === "input" ? issue : null;
}
